import { Alert, Button, Card, Form, Input, Space, message } from 'antd';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import LmsUnitSelect from '@/pages/education/components/LmsUnitSelect';
import { EducationSectionSelect, EducationSubjectSelect } from '@/pages/education/components/EducationLookupSelects';
import { lmsErrorMessage, useLmsAccess } from '@/pages/education/utils/lmsAccess';

type Resource = {
  id: string; section_id?: string | null; subject_id?: string | null; curriculum_unit_id?: string | null;
  title: string; resource_url: string; notes?: string | null;
};
type Values = {
  section_id?: string; subject_id?: string; curriculum_unit_id?: string;
  title: string; resource_url: string; notes?: string;
};

const LIST_PATH = '/education/lms/resources';

export default function ResourceFormPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canManage } = useLmsAccess();
  const [form] = Form.useForm<Values>();
  const subjectId = Form.useWatch('subject_id', form);

  const detail = useQuery({
    queryKey: ['education-lms-resource', id],
    enabled: !isNew,
    queryFn: async () => (await api.get<Resource>(`/education/lms/resources/${id}`)).data,
  });
  const resource = detail.data;
  useEffect(() => {
    if (!resource) return;
    form.setFieldsValue({
      section_id: resource.section_id || undefined,
      subject_id: resource.subject_id || undefined,
      curriculum_unit_id: resource.curriculum_unit_id || undefined,
      title: resource.title,
      resource_url: resource.resource_url,
      notes: resource.notes || undefined,
    });
  }, [resource, form]);

  const save = useMutation({
    mutationFn: (values: Values) => {
      const payload = {
        section_id: values.section_id || null,
        subject_id: values.subject_id || null,
        curriculum_unit_id: values.curriculum_unit_id || null,
        title: values.title,
        resource_url: values.resource_url.trim(),
        notes: values.notes?.trim() || null,
      };
      return isNew ? api.post('/education/lms/resources', payload) : api.put(`/education/lms/resources/${id}`, payload);
    },
    onSuccess: () => {
      message.success(t('lmsSaved', { defaultValue: 'Saved' }));
      queryClient.invalidateQueries({ queryKey: ['education-lms-resources'] });
      queryClient.invalidateQueries({ queryKey: ['education-lms-resource', id] });
      queryClient.invalidateQueries({ queryKey: ['education-lms-summary'] });
      navigate(LIST_PATH);
    },
    onError: (e) => message.error(lmsErrorMessage(e, 'Unable to save resource')),
  });

  return (
    <EducationPageChrome
      title={isNew ? t('lmsNewResource', { defaultValue: 'New resource' }) : t('lmsEditResource', { defaultValue: 'Edit resource' })}
      extra={<Button onClick={() => navigate(LIST_PATH)}>{t('lmsResources', { defaultValue: 'Resources' })}</Button>}
    >
      {!canManage ? <Alert type="warning" showIcon message={t('lmsReadOnly', { defaultValue: 'Read-only access.' })} /> : null}
      <Card size="small" style={{ maxWidth: 820 }} loading={!isNew && detail.isLoading}>
        <Form<Values> form={form} layout="vertical" disabled={!canManage} onFinish={(v) => save.mutate(v)}>
          <Form.Item name="title" label={t('title', { defaultValue: 'Title' })} rules={[{ required: true, max: 250 }]}>
            <Input maxLength={250} />
          </Form.Item>
          <Form.Item
            name="resource_url" label={t('lmsResourceUrl', { defaultValue: 'Resource link (http/https)' })}
            rules={[{ required: true }, { type: 'url', message: 'Enter a valid URL' }]}
          >
            <Input maxLength={1000} placeholder="https://…" />
          </Form.Item>
          <Form.Item name="section_id" label={t('section', { defaultValue: 'Section' })}>
            <EducationSectionSelect allowClear />
          </Form.Item>
          <Form.Item name="subject_id" label={t('subject', { defaultValue: 'Subject' })}>
            <EducationSubjectSelect allowClear onChange={() => form.setFieldValue('curriculum_unit_id', undefined)} />
          </Form.Item>
          <Form.Item name="curriculum_unit_id" label={t('lmsCurriculumUnit', { defaultValue: 'Curriculum unit (optional)' })}>
            <LmsUnitSelect subjectId={subjectId} />
          </Form.Item>
          <Form.Item name="notes" label={t('lmsNotes', { defaultValue: 'Notes' })}>
            <Input.TextArea rows={4} maxLength={2000} showCount />
          </Form.Item>
          {canManage ? (
            <Space>
              <Button type="primary" htmlType="submit" loading={save.isPending}>{t('save', { defaultValue: 'Save' })}</Button>
              <Button onClick={() => navigate(LIST_PATH)}>{t('cancel', { defaultValue: 'Cancel' })}</Button>
            </Space>
          ) : null}
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
